import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Color, Grid, Type } from '../../../app/StyleGuide';
import { getScoreLevel, SCORE_COLORS, SCORE_LEVEL_LABELS } from '../utils/scoreUtils';
import type { CategoryBreakdown, CategoryQuestion, KeyInsight } from '../models/assessmentReportView';
import { sanitizeHTML } from '../../../utils/sanitizeHTML';

const PrintStyles = createGlobalStyle`
  @media print {
    nav[role='navigation'],
    #alerts-banner-container {
      display: none !important;
    }
  }
`;

const PrintContainer = styled.div`
  display: none;

  @media print {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
    padding: 0;
    overflow: visible;
    color: ${Type.Color.dark};
    font-size: 10pt;
    line-height: 1.3;
  }
`;

const PrintHeader = styled.header`
  margin-bottom: ${Grid._4};
  padding-bottom: ${Grid._3};
  border-bottom: 1px solid ${Color.Gray._30};
  break-inside: avoid;
  page-break-inside: avoid;
`;

const PrintTitle = styled.h1`
  margin: 0 0 ${Grid._1};
  font-size: 16pt;
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const PrintSubtitle = styled.p`
  margin: 0;
  font-size: 9pt;
  color: ${Type.Color.medium};
`;

const AboutSection = styled.section`
  margin-top: ${Grid._3};
`;

const AboutTitle = styled.h2`
  margin: 0 0 ${Grid._1};
  font-size: ${Type.Scale._05};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const AboutIntroduction = styled.p`
  margin: 0;
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
  line-height: 1.35;
`;

const Section = styled.section`
  margin-bottom: ${Grid._4};
`;

const SectionTitle = styled.h2`
  margin: 0 0 ${Grid._2};
  font-size: ${Type.Scale._05};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  break-after: avoid;
  page-break-after: avoid;
`;

const SectionText = styled.p`
  margin: 0;
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
  line-height: 1.35;
`;

const OverallScoreRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${Grid._3};
  margin-bottom: ${Grid._1};
`;

const OverallScoreValue = styled.span`
  font-size: ${Type.Scale._5};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: 1;
`;

const OverallScoreMeta = styled.div`
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${Grid._2};
  break-inside: avoid;
  page-break-inside: avoid;
`;

const InsightCell = styled.div`
  padding: ${Grid._2};
  border: 1px solid ${Color.Gray._20};
  border-radius: 3px;
`;

const InsightLabel = styled.span`
  display: block;
  margin-bottom: 1px;
  font-size: ${Type.Scale._05};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.medium};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const InsightValue = styled.span`
  display: block;
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const InsightDetail = styled.span`
  display: block;
  margin-top: 1px;
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
  line-height: 1.3;
`;

const CategoryBlock = styled.div`
  margin-bottom: ${Grid._3};
  border: 1px solid ${Color.Gray._20};
  border-radius: 3px;
  break-inside: auto;
  page-break-inside: auto;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryBlockHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${Grid._2} ${Grid._3};
  padding: ${Grid._2} ${Grid._3};
  background: ${Color.Gray._10};
  border-bottom: 1px solid ${Color.Gray._20};
  break-inside: avoid;
  page-break-inside: avoid;
  break-after: avoid;
  page-break-after: avoid;
`;

const CategoryBlockTitle = styled.h3`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: ${Type.Scale._05};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const CategoryBlockMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
  white-space: nowrap;
`;

const CategoryBlockDescription = styled.p`
  width: 100%;
  margin: 0;
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
  line-height: 1.3;
`;

const CategoryQuestions = styled.div`
  padding: 0 ${Grid._3};
`;

const QuestionBlock = styled.div`
  padding: ${Grid._4} 0;
  border-bottom: 1px solid ${Color.Gray._20};
  break-inside: avoid;
  page-break-inside: avoid;

  &:last-child {
    border-bottom: none;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${Grid._2};
  margin-bottom: ${Grid._1};
`;

const QuestionText = styled.p`
  flex: 1;
  margin: 0;
  font-size: ${Type.Scale._05};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};
  line-height: 1.3;
`;

const QuestionScore = styled.span`
  font-size: ${Type.Scale._05};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  white-space: nowrap;
`;

const ResponseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ResponseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1px ${Grid._2};
  align-items: center;
`;

const ResponseLabel = styled.span`
  grid-column: 1;
  font-size: ${Type.Scale._05};
  color: ${Type.Color.dark};
`;

const ResponseStats = styled.div`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  align-items: baseline;
  gap: ${Grid._2};
  white-space: nowrap;
  font-size: ${Type.Scale._05};
`;

const ResponseCount = styled.span`
  color: ${Type.Color.medium};
`;

const ResponsePercent = styled.span`
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const ResponseBar = styled.div`
  grid-column: 1 / -1;
  width: 100%;
  min-height: ${Grid._3};
  line-height: 0;
`;

const PrintProgressBar = ({ progress }: { progress: number }) => (
  <ResponseBar aria-hidden="true">
    <svg width="100%" height="4" viewBox="0 0 100 4" preserveAspectRatio="none" role="presentation">
      <rect width="100" height="4" fill={Color.Gray._20} />
      <rect width={Math.max(0, Math.min(progress, 100))} height="4" fill={Color.Blue._50} />
    </svg>
  </ResponseBar>
);

const CategoryScore = styled.span<{ $level: keyof typeof SCORE_COLORS }>`
  display: inline-block;
  min-width: 2rem;
  padding: 1px 4px;
  border-radius: 2px;
  background: ${props => SCORE_COLORS[props.$level]};
  color: ${Color.white};
  font-size: ${Type.Scale._05};
  font-weight: ${Type.Weight.bold};
  text-align: center;
`;

const ScoreLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Grid._3};
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
  margin-bottom: ${Grid._2};
`;

const ScoreLegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Grid._1};
`;

const ScoreLegendSwatch = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 1px;
  background: ${props => props.$color};
`;

const CategoryBlockHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._1};
`;

const getInsightDisplay = (insight: KeyInsight) => {
  if (insight.highlight) {
    return {
      value: insight.highlight.value,
      detail: insight.highlight.label,
    };
  }

  if (insight.scoreMetric) {
    const maxScore = insight.scoreMetric.maxScore ?? 5;
    return {
      value: `${insight.scoreMetric.score.toFixed(2)}/${maxScore}`,
      detail: undefined,
    };
  }

  if (insight.metric) {
    return { value: insight.metric, detail: undefined };
  }

  return { value: '—', detail: undefined };
};

type AssessmentReportPrintableProps = {
  title: string;
  subtitle: string;
  introduction: string;
  overallScore: number;
  overallSummary: string;
  insights: KeyInsight[];
  categories: CategoryBreakdown[];
  uncategorizedQuestions?: CategoryQuestion[];
};

export const AssessmentReportPrintable = ({
  title,
  subtitle,
  introduction,
  overallScore,
  overallSummary,
  insights,
  categories,
  uncategorizedQuestions = [],
}: AssessmentReportPrintableProps) => {
  const scoreLevel = getScoreLevel(overallScore);

  return (
    <>
      <PrintStyles />
      <PrintContainer className="assessment-report-print">
        <PrintHeader>
          <PrintTitle>{title}</PrintTitle>
          <PrintSubtitle>{subtitle}</PrintSubtitle>
          <AboutSection>
            <AboutTitle>About This Assessment</AboutTitle>
            <AboutIntroduction dangerouslySetInnerHTML={{ __html: sanitizeHTML(introduction) }} />
          </AboutSection>
        </PrintHeader>

        <Section>
          <SectionTitle>Overall Assessment Score</SectionTitle>
          <OverallScoreRow>
            <OverallScoreValue>{overallScore.toFixed(2)}</OverallScoreValue>
            <OverallScoreMeta>
              <div>out of 5</div>
            </OverallScoreMeta>
          </OverallScoreRow>
          <SectionText>
            <strong>{SCORE_LEVEL_LABELS[scoreLevel]}</strong>
            <br />
            {overallSummary}
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>Key Insights</SectionTitle>
          <InsightsGrid>
            {insights.map(insight => {
              const { value, detail } = getInsightDisplay(insight);
              return (
                <InsightCell key={insight.title}>
                  <InsightLabel>{insight.title}</InsightLabel>
                  <InsightValue>{value}</InsightValue>
                  {detail && <InsightDetail>{detail}</InsightDetail>}
                  <InsightDetail>{insight.description}</InsightDetail>
                </InsightCell>
              );
            })}
          </InsightsGrid>
        </Section>

        <Section>
          <SectionTitle>Category Breakdown</SectionTitle>
          <ScoreLegend>
            <ScoreLegendItem>
              <ScoreLegendSwatch $color={SCORE_COLORS.consistent} />
              Consistent 5.0–4.0
            </ScoreLegendItem>
            <ScoreLegendItem>
              <ScoreLegendSwatch $color={SCORE_COLORS.moderate} />
              Moderate 3.9–2.0
            </ScoreLegendItem>
            <ScoreLegendItem>
              <ScoreLegendSwatch $color={SCORE_COLORS.limited} />
              Limited 1.9–0.0
            </ScoreLegendItem>
          </ScoreLegend>
          {categories.map(category => (
            <CategoryBlock key={category.title}>
              <CategoryBlockHeader>
                <CategoryBlockHeaderLeft>
                  <CategoryBlockTitle>{category.title}</CategoryBlockTitle>

                  <CategoryBlockDescription>{category.description}</CategoryBlockDescription>
                </CategoryBlockHeaderLeft>
                <CategoryBlockMeta>
                  <CategoryScore $level={getScoreLevel(category.score)}>{category.score.toFixed(2)}</CategoryScore>
                  <span>
                    {category.idealPercent}% ideal · {category.positivePercent}% positive
                  </span>
                </CategoryBlockMeta>
              </CategoryBlockHeader>
              <CategoryQuestions>
                {category.questions.map((question, index) => (
                  <QuestionBlock key={question.text}>
                    <QuestionHeader>
                      <QuestionText>
                        {index + 1}. {question.text}
                      </QuestionText>
                      <QuestionScore>{question.score.toFixed(2)} / 5</QuestionScore>
                    </QuestionHeader>
                    <ResponseList>
                      {question.responses.map(response => (
                        <ResponseRow key={response.label}>
                          <ResponseLabel>{response.label}</ResponseLabel>
                          <ResponseStats>
                            <ResponseCount>
                              {response.count} response{response.count === 1 ? '' : 's'}
                            </ResponseCount>
                            <ResponsePercent>{response.percent}%</ResponsePercent>
                          </ResponseStats>
                          <PrintProgressBar progress={response.percent} />
                        </ResponseRow>
                      ))}
                    </ResponseList>
                  </QuestionBlock>
                ))}
              </CategoryQuestions>
            </CategoryBlock>
          ))}
          {uncategorizedQuestions.length > 0 && (
            <>
              <SectionTitle>Additional Questions</SectionTitle>
              <CategoryQuestions>
                {uncategorizedQuestions.map((question, index) => (
                  <QuestionBlock key={question.text}>
                    <QuestionHeader>
                      <QuestionText>
                        {index + 1}. {question.text}
                      </QuestionText>
                      <QuestionScore>{question.score.toFixed(2)} / 5</QuestionScore>
                    </QuestionHeader>
                    <ResponseList>
                      {question.responses.map(response => (
                        <ResponseRow key={response.label}>
                          <ResponseLabel>{response.label}</ResponseLabel>
                          <ResponseStats>
                            <ResponseCount>
                              {response.count} response{response.count === 1 ? '' : 's'}
                            </ResponseCount>
                            <ResponsePercent>{response.percent}%</ResponsePercent>
                          </ResponseStats>
                          <PrintProgressBar progress={response.percent} />
                        </ResponseRow>
                      ))}
                    </ResponseList>
                  </QuestionBlock>
                ))}
              </CategoryQuestions>
            </>
          )}
        </Section>
      </PrintContainer>
    </>
  );
};

export default AssessmentReportPrintable;
